import { validateMerchant, authorizePayment } from '../../../api/applePay'
import { config } from '../../../config'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import { SUCCESS_RESPONSES } from '../../../constants/serverResponses'
import { loadScript, redirect } from '../../../helpers/common'
import { DataDog } from '../../../datadog'
import { RESPONSE_ERROR_TEXT } from '../../../helpers/serverTextResponses'

const APPLE_PAY_DEFAULT_NETWORKS = ['visa', 'mastercard', 'amex', 'discover']
export const mapAppleNetworksToProhibitedCard = {
  amex: 'AmEx',
  mastercard: 'MasterCard',
  visa: 'Visa',
  discover: 'discover',
}

export const loadApplePayScript = () =>
  new Promise((resolve, reject) => {
    loadScript(
      config.applePayURL,
      () => {
        resolve()
      },
      () => {
        console.error('Loading Apple Pay script error')
        reject()
      },
      false,
    )
  })

export function handleApplePay(transactionDetails, setError, l10n) {
  const { ApplePaySession } = window
  if (!ApplePaySession) {
    return
  }

  let supportedNetworks = Object.values(APPLE_PAY_DEFAULT_NETWORKS)

  if (transactionDetails?.prohibitedCardBrands?.length) {
    supportedNetworks = supportedNetworks.filter(
      network =>
        !transactionDetails.prohibitedCardBrands.includes(
          mapAppleNetworksToProhibitedCard[network],
        ),
    )
  }

  const request = {
    countryCode: transactionDetails.countryCode,
    currencyCode: transactionDetails.currency,
    applicationData: window.btoa(transactionDetails.salesOrderId),
    merchantCapabilities: ['supports3DS'],
    supportedNetworks,
    requiredBillingContactFields: ['postalAddress'],
    requiredShippingContactFields: ['email'],
    total: {
      label: 'TUI Trip',
      type: 'final',
      amount: transactionDetails?.overrideAmount
        ? transactionDetails.overrideAmount / 100
        : transactionDetails.amount / 100,
    },
  }
  DataDog.info('Making ApplePaySession instance with request data', request)

  const session = new ApplePaySession(3, request)

  session.onvalidatemerchant = async event => {
    DataDog.info('Apple Pay onvalidatemerchant callback')
    const { responseData } = await validateMerchant(event.validationURL)
    session.completeMerchantValidation(responseData)
  }

  session.onpaymentmethodselected = () => {
    DataDog.info('Apple Pay onpaymentmethodselected callback')
    const update = {
      newTotal: request.total,
    }
    session.completePaymentMethodSelection(update)
  }

  session.onshippingmethodselected = () => {
    DataDog.info('Apple Pay onshippingmethodselected callback')
    const update = {}
    session.completeShippingMethodSelection(update)
  }

  session.onshippingcontactselected = () => {
    DataDog.info('Apple Pay onshippingcontactselected callback')
    const update = {}
    session.completeShippingContactSelection(update)
  }

  session.onpaymentauthorized = async event => {
    DataDog.info('Apple Pay onpaymentauthorized callback')
    const mfeElement = window.tuiPaymentsMFEInstance

    const continuePayment = async overrideValues => {
      const overrideTransactionDetails = transactionDetails

      if (overrideValues && transactionDetails?.billingAddress) {
        overrideTransactionDetails.billingAddress = {
          ...transactionDetails.billingAddress,
          ...overrideValues.billingAddress,
        }
      }

      const { responseData, responseStatus } = await authorizePayment(
        overrideTransactionDetails,
        event.payment,
      )
      const isSuccess = responseStatus === SUCCESS_RESPONSES.OK
      const result = {
        status: isSuccess
          ? ApplePaySession.STATUS_SUCCESS
          : ApplePaySession.STATUS_FAILURE,
      }

      session.completePayment(result)

      if (isSuccess) {
        DataDog.info(
          `Redirection. Component: ApplePay (handler), redirect url: ${transactionDetails.successUrl}`,
        )
        mfeElement.onAuthorised?.()
        redirect(transactionDetails.successUrl)
      } else {
        setError(
          RESPONSE_ERROR_TEXT(l10n, responseStatus),
          responseStatus,
          responseData,
        )
      }
    }

    if (mfeElement?.onAuthorising) {
      DataDog.info(`onAuthorising hook has been enabled`)

      mfeElement
        .onAuthorising?.({
          paymentMethod: PAYMENT_METHOD_TYPES.ApplePay,
          billingAddress: transactionDetails.billingAddress || {},
        })
        .then(async overrideValues => {
          DataDog.info(`onAuthorising Promise resolved`)
          await continuePayment(overrideValues)
        })
        .catch(() => {
          DataDog.info(`onAuthorising Promise rejected`)
          console.log('onAuthorising Promise rejected')
        })
    } else {
      await continuePayment()
    }
  }

  session.oncancel = e => {
    DataDog.info('Apple Pay oncancel callback', e?.sessionError)
    console.log('oncancel', e)
  }

  session.begin()
}
